import { Container, Label } from '@playcanvas/pcui';
import { Mat4, path, Vec3 } from 'playcanvas';

import { AnnotationPanel } from './annotation-panel';
import { DataPanel } from './data-panel';
import { EvidencePanel } from './evidence-panel';
import { Events } from '../events';
import { AboutPopup } from './about-popup';
import { BottomToolbar } from './bottom-toolbar';
import { CameraMotionImportDialog } from './camera-motion-import-dialog';
import { CasePanel } from './case-panel';
import { ColorPanel } from './color-panel';
import { ComparisonSlider } from './comparison-slider';
import { ExportPopup } from './export-popup';
import { ImageSettingsDialog } from './image-settings-dialog';
import { localize, localizeInit } from './localization';
import { Menu } from './menu';
import { ModeToggle } from './mode-toggle';
import { OrbitDialog } from './orbit-dialog';
import { Popup, ShowOptions } from './popup';
import { PresentationHUD } from './presentation-hud';
import { Progress } from './progress';
import { PublishSettingsDialog } from './publish-settings-dialog';
import { RightToolbar } from './right-toolbar';
import { ScenePanel } from './scene-panel';
import { ShortcutsPopup } from './shortcuts-popup';
import { SpeedHud } from './speed-hud';
import { Spinner } from './spinner';
import { StatusBar } from './status-bar';
import { TimeDisplay } from './time-display';
import { TimelinePanel } from './timeline-panel';
import { Tooltips } from './tooltips';
import { VehicleMotionImportDialog } from './vehicle-motion-import-dialog';
import { VideoSettingsDialog } from './video-settings-dialog';
import { ViewCube } from './view-cube';
import { ViewPanel } from './view-panel';
import { WorkspaceSwitcher } from './workspace-switcher';
import type { WorkspaceMode, WorkspacePanel } from './workspace-switcher';
import { version } from '../../package.json';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

const removeExtension = (filename: string) => {
    return filename.substring(0, filename.length - path.getExtension(filename).length);
};

const makePanelResizable = (panel: Container, options: { edge: 'left' | 'right'; minWidth?: number; maxWidth?: number }) => {
    const handle = document.createElement('div');
    handle.className = `panel-resize-handle panel-resize-handle-${options.edge}`;
    panel.dom.appendChild(handle);

    let resizing = false;
    let startX = 0;
    let startWidth = 0;

    const getMaxWidth = () => options.maxWidth ?? Math.max(320, Math.floor(window.innerWidth * 0.65));

    const finish = (pointerId: number | null) => {
        if (pointerId !== null && handle.hasPointerCapture(pointerId)) {
            handle.releasePointerCapture(pointerId);
        }
        resizing = false;
    };

    handle.addEventListener('pointerdown', (event: PointerEvent) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
            return;
        }

        resizing = true;
        startX = event.clientX;
        startWidth = panel.dom.getBoundingClientRect().width;
        handle.setPointerCapture(event.pointerId);
        event.preventDefault();
        event.stopPropagation();
    });

    handle.addEventListener('pointermove', (event: PointerEvent) => {
        if (!resizing) {
            return;
        }

        const delta = options.edge === 'right' ? event.clientX - startX : startX - event.clientX;
        const minWidth = options.minWidth ?? 240;
        const width = Math.max(minWidth, Math.min(getMaxWidth(), startWidth + delta));
        panel.style.width = `${Math.round(width)}px`;
        event.preventDefault();
        event.stopPropagation();
    });

    handle.addEventListener('pointerup', (event: PointerEvent) => {
        if (resizing && event.isPrimary) {
            finish(event.pointerId);
            event.preventDefault();
            event.stopPropagation();
        }
    });

    handle.addEventListener('lostpointercapture', () => {
        resizing = false;
    });
};

class EditorUI {
    appContainer: Container;
    topContainer: Container;
    canvasContainer: Container;
    toolsContainer: Container;
    canvas: HTMLCanvasElement;
    popup: Popup;

    constructor(events: Events) {
        // app
        const appContainer = new Container({
            id: 'app-container'
        });

        // editor
        const editorContainer = new Container({
            id: 'editor-container'
        });

        // tooltips container
        const tooltipsContainer = new Container({
            id: 'tooltips-container'
        });

        // top container
        const topContainer = new Container({
            id: 'top-container'
        });

        // canvas
        const canvas = document.createElement('canvas');
        canvas.id = 'canvas';

        // app label
        const appLabel = new Label({
            id: 'app-label',
            text: `NSite.ai Editor v${version}`
        });

        // cursor label
        const cursorLabel = new Label({
            id: 'cursor-label'
        });

        let fullprecision = '';

        events.on('camera.focalPointPicked', (details: { position: Vec3 }) => {
            cursorLabel.text = `${details.position.x.toFixed(2)}, ${details.position.y.toFixed(2)}, ${details.position.z.toFixed(2)}`;
            fullprecision = `${details.position.x}, ${details.position.y}, ${details.position.z}`;
        });

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            cursorLabel.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        cursorLabel.dom.addEventListener('pointerdown', () => {
            navigator.clipboard.writeText(fullprecision);

            const orig = cursorLabel.text;
            cursorLabel.text = localize('cursor.copied');
            setTimeout(() => {
                cursorLabel.text = orig;
            }, 1000);
        });

        // canvas container
        const canvasContainer = new Container({
            id: 'canvas-container'
        });

        // tools container
        const toolsContainer = new Container({
            id: 'tools-container'
        });

        // tooltips
        const tooltips = new Tooltips();
        tooltipsContainer.append(tooltips);

        // bottom toolbar
        const scenePanel = new ScenePanel(events, tooltips);
        const viewPanel = new ViewPanel(events, tooltips);
        const colorPanel = new ColorPanel(events, tooltips);
        const casePanel = new CasePanel(events);
        const evidencePanel = new EvidencePanel(events);
        const annotationPanel = new AnnotationPanel(events);
        const comparisonSlider = new ComparisonSlider(events);
        const bottomToolbar = new BottomToolbar(events, tooltips);
        const rightToolbar = new RightToolbar(events, tooltips);
        const modeToggle = new ModeToggle(events, tooltips);
        const menu = new Menu(events);
        const presentationHUD = new PresentationHUD(events);
        const speedHud = new SpeedHud(events);
        const timeDisplay = new TimeDisplay(events);
        const experimentalWorkspaceUi = new URLSearchParams(window.location.search).get('uiRefresh') === '1';
        let workspaceSwitcher: WorkspaceSwitcher | null = null;

        makePanelResizable(scenePanel, { edge: 'right', minWidth: 260 });
        makePanelResizable(viewPanel, { edge: 'left', minWidth: 280 });
        makePanelResizable(colorPanel, { edge: 'left', minWidth: 280 });
        makePanelResizable(casePanel, { edge: 'left', minWidth: 280 });
        makePanelResizable(evidencePanel, { edge: 'left', minWidth: 300 });
        makePanelResizable(annotationPanel, { edge: 'left', minWidth: 320 });

        canvasContainer.dom.appendChild(canvas);
        canvasContainer.append(appLabel);
        canvasContainer.append(cursorLabel);
        canvasContainer.append(timeDisplay);
        canvasContainer.append(speedHud);
        canvasContainer.append(toolsContainer);
        canvasContainer.append(scenePanel);
        canvasContainer.append(viewPanel);
        canvasContainer.append(colorPanel);
        canvasContainer.append(casePanel);
        canvasContainer.append(evidencePanel);
        canvasContainer.append(annotationPanel);
        canvasContainer.append(comparisonSlider);
        canvasContainer.append(bottomToolbar);
        canvasContainer.append(rightToolbar);
        canvasContainer.append(modeToggle);
        canvasContainer.append(menu);
        canvasContainer.dom.appendChild(presentationHUD.dom);

        // view axes container
        const viewCube = new ViewCube(events);
        canvasContainer.append(viewCube);
        events.on('prerender', (cameraMatrix: Mat4) => {
            viewCube.update(cameraMatrix);
        });

        // main container
        const mainContainer = new Container({
            id: 'main-container'
        });

        const timelinePanel = new TimelinePanel(events, tooltips);
        const dataPanel = new DataPanel(events);
        const statusBar = new StatusBar(events, tooltips);

        let currentWorkspaceMode: WorkspaceMode = 'setup';
        let currentWorkspacePanel: WorkspacePanel = 'scene';

        const closeRightPanels = () => {
            events.fire('viewPanel.setVisible', false);
            events.fire('colorPanel.setVisible', false);
            events.fire('casePanel.setVisible', false);
            events.fire('evidencePanel.setVisible', false);
            events.fire('annotationPanel.setVisible', false);
        };

        const setTimelineWorkspaceVisible = (visible: boolean) => {
            events.fire('statusBar.panelChanged', visible ? 'timeline' : null);
        };

        const applyWorkspacePanel = (panel: WorkspacePanel) => {
            currentWorkspacePanel = panel;

            switch (currentWorkspaceMode) {
                case 'setup':
                    scenePanel.hidden = false;
                    setTimelineWorkspaceVisible(false);
                    events.fire('casePanel.setVisible', false);
                    events.fire('evidencePanel.setVisible', false);
                    events.fire('annotationPanel.setVisible', false);

                    if (panel === 'view') {
                        events.fire('colorPanel.setVisible', false);
                        events.fire('viewPanel.setVisible', true);
                    } else if (panel === 'color') {
                        events.fire('viewPanel.setVisible', false);
                        events.fire('colorPanel.setVisible', true);
                    } else {
                        events.fire('viewPanel.setVisible', false);
                        events.fire('colorPanel.setVisible', false);
                    }
                    break;

                case 'animate':
                    scenePanel.hidden = false;
                    setTimelineWorkspaceVisible(true);
                    events.fire('casePanel.setVisible', false);
                    events.fire('evidencePanel.setVisible', false);
                    events.fire('annotationPanel.setVisible', false);

                    if (panel === 'view') {
                        events.fire('colorPanel.setVisible', false);
                        events.fire('viewPanel.setVisible', true);
                    } else {
                        events.fire('viewPanel.setVisible', false);
                        events.fire('colorPanel.setVisible', false);
                    }
                    break;

                case 'review':
                    scenePanel.hidden = true;
                    setTimelineWorkspaceVisible(false);
                    closeRightPanels();

                    if (panel === 'case') {
                        events.fire('casePanel.setVisible', true);
                    } else if (panel === 'annotation') {
                        events.fire('annotationPanel.setVisible', true);
                    } else {
                        currentWorkspacePanel = 'evidence';
                        events.fire('evidencePanel.setVisible', true);
                    }
                    break;
            }

            workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
        };

        const applyWorkspaceMode = (mode: WorkspaceMode) => {
            currentWorkspaceMode = mode;

            document.body.classList.toggle('experimental-ui-refresh', experimentalWorkspaceUi);
            document.body.classList.remove('workspace-setup', 'workspace-animate', 'workspace-review');
            document.body.classList.add(`workspace-${mode}`);

            if (mode === 'setup') {
                applyWorkspacePanel('scene');
            } else if (mode === 'animate') {
                applyWorkspacePanel('timeline');
            } else {
                applyWorkspacePanel('evidence');
            }
        };

        if (experimentalWorkspaceUi) {
            workspaceSwitcher = new WorkspaceSwitcher({
                onModeChange: applyWorkspaceMode,
                onPanelSelect: applyWorkspacePanel
            });

            canvasContainer.append(workspaceSwitcher);
        }

        timelinePanel.hidden = true;

        mainContainer.append(canvasContainer);
        mainContainer.append(timelinePanel);
        mainContainer.append(dataPanel);
        mainContainer.append(statusBar);

        // Wire up status bar panel toggles
        events.on('statusBar.panelChanged', (panel: string | null) => {
            timelinePanel.hidden = panel !== 'timeline';
            dataPanel.hidden = panel !== 'splatData';
        });

        editorContainer.append(mainContainer);

        const setPresentationMode = (enabled: boolean) => {
            document.body.classList.toggle('presentation-mode', enabled);

            appLabel.hidden = enabled;
            cursorLabel.hidden = enabled;
            scenePanel.hidden = enabled;
            viewPanel.hidden = enabled;
            colorPanel.hidden = enabled;
            casePanel.hidden = enabled;
            evidencePanel.hidden = enabled;
            annotationPanel.hidden = enabled;
            bottomToolbar.hidden = enabled;
            rightToolbar.hidden = enabled;
            modeToggle.hidden = enabled || experimentalWorkspaceUi;
            if (workspaceSwitcher) {
                workspaceSwitcher.hidden = enabled;
            }
            menu.hidden = enabled;
            viewCube.hidden = enabled;
            timelinePanel.hidden = enabled;
            dataPanel.hidden = enabled;
            statusBar.hidden = enabled;
            tooltipsContainer.hidden = enabled;
            topContainer.hidden = enabled;
        };

        events.on('presentation.modeChanged', (enabled: boolean) => {
            setPresentationMode(!!enabled);
        });

        if (experimentalWorkspaceUi) {
            modeToggle.hidden = true;

            events.on('viewPanel.visible', (visible: boolean) => {
                if (currentWorkspaceMode !== 'review' && visible) {
                    currentWorkspacePanel = 'view';
                    workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
                }
            });

            events.on('colorPanel.visible', (visible: boolean) => {
                if (currentWorkspaceMode === 'setup' && visible) {
                    currentWorkspacePanel = 'color';
                    workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
                }
            });

            events.on('casePanel.visible', (visible: boolean) => {
                if (currentWorkspaceMode === 'review' && visible) {
                    currentWorkspacePanel = 'case';
                    workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
                }
            });

            events.on('evidencePanel.visible', (visible: boolean) => {
                if (currentWorkspaceMode === 'review' && visible) {
                    currentWorkspacePanel = 'evidence';
                    workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
                }
            });

            events.on('annotationPanel.visible', (visible: boolean) => {
                if (currentWorkspaceMode === 'review' && visible) {
                    currentWorkspacePanel = 'annotation';
                    workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
                }
            });

            events.on('statusBar.panelChanged', (panel: string | null) => {
                if (currentWorkspaceMode === 'animate' && panel === 'timeline') {
                    currentWorkspacePanel = 'timeline';
                    workspaceSwitcher?.refresh(currentWorkspaceMode, currentWorkspacePanel);
                }
            });

            applyWorkspaceMode('setup');
        }

        tooltips.register(cursorLabel, localize('cursor.click-to-copy'), 'top');

        // message popup
        const popup = new Popup(tooltips);

        // shortcuts popup
        const shortcutsPopup = new ShortcutsPopup(events);

        // export popup
        const exportPopup = new ExportPopup(events);

        // publish settings
        const publishSettingsDialog = new PublishSettingsDialog(events);

        // image settings
        const imageSettingsDialog = new ImageSettingsDialog(events);

        // video settings
        const videoSettingsDialog = new VideoSettingsDialog(events);

        // vehicle motion import
        const vehicleMotionImportDialog = new VehicleMotionImportDialog(events);

        // camera motion import
        const cameraMotionImportDialog = new CameraMotionImportDialog(events);

        // orbit generation
        const orbitDialog = new OrbitDialog(events);

        // about popup
        const aboutPopup = new AboutPopup();

        topContainer.append(popup);
        topContainer.append(exportPopup);
        topContainer.append(publishSettingsDialog);
        topContainer.append(imageSettingsDialog);
        topContainer.append(videoSettingsDialog);
        topContainer.append(cameraMotionImportDialog);
        topContainer.append(orbitDialog);
        topContainer.append(vehicleMotionImportDialog);
        topContainer.append(shortcutsPopup);
        topContainer.append(aboutPopup);

        appContainer.append(editorContainer);
        appContainer.append(topContainer);
        appContainer.append(tooltipsContainer);

        this.appContainer = appContainer;
        this.topContainer = topContainer;
        this.canvasContainer = canvasContainer;
        this.toolsContainer = toolsContainer;
        this.canvas = canvas;
        this.popup = popup;

        document.body.appendChild(appContainer.dom);
        document.body.setAttribute('tabIndex', '-1');

        events.on('show.shortcuts', () => {
            shortcutsPopup.hidden = false;
        });

        events.function('show.exportPopup', (exportType, splatNames: [string], showFilenameEdit: boolean) => {
            return exportPopup.show(exportType, splatNames, showFilenameEdit);
        });

        events.function('show.publishSettingsDialog', async () => {
            // show popup if user isn't logged in
            const userStatus = await events.invoke('publish.userStatus');
            if (!userStatus) {
                await events.invoke('showPopup', {
                    type: 'error',
                    header: localize('popup.error'),
                    message: localize('popup.publish.please-log-in')
                });
                return false;
            }

            // get user publish settings
            const publishSettings = await publishSettingsDialog.show(userStatus);

            // do publish
            if (publishSettings) {
                await events.invoke('scene.publish', publishSettings);
            }
        });

        events.function('show.imageSettingsDialog', async () => {
            const imageSettings = await imageSettingsDialog.show();

            if (imageSettings) {
                await events.invoke('render.image', imageSettings);
            }
        });

        events.function('show.videoSettingsDialog', async () => {
            const videoSettings = await videoSettingsDialog.show();

            if (videoSettings) {

                try {
                    const docName = events.invoke('doc.name');

                    // Determine file extension and mime type based on format
                    let fileExtension: string;
                    let filePickerTypes: FilePickerAcceptType[];

                    // Codec name mapping for display
                    const codecNames: Record<string, string> = {
                        'h264': 'H.264',
                        'h265': 'H.265',
                        'vp9': 'VP9',
                        'av1': 'AV1'
                    };
                    const codecName = codecNames[videoSettings.codec] || videoSettings.codec.toUpperCase();

                    if (videoSettings.format === 'webm') {
                        fileExtension = '.webm';
                        filePickerTypes = [{
                            description: `WebM Video (${codecName})`,
                            accept: { 'video/webm': ['.webm'] }
                        }];
                    } else if (videoSettings.format === 'mov') {
                        fileExtension = '.mov';
                        filePickerTypes = [{
                            description: `MOV Video (${codecName})`,
                            accept: { 'video/quicktime': ['.mov'] }
                        }];
                    } else if (videoSettings.format === 'mkv') {
                        fileExtension = '.mkv';
                        filePickerTypes = [{
                            description: `MKV Video (${codecName})`,
                            accept: { 'video/x-matroska': ['.mkv'] }
                        }];
                    } else {
                        fileExtension = '.mp4';
                        filePickerTypes = [{
                            description: `MP4 Video (${codecName})`,
                            accept: { 'video/mp4': ['.mp4'] }
                        }];
                    }

                    const suggested = `${removeExtension(docName ?? 'supersplat')}${fileExtension}`;

                    let writable;
                    let fileHandle: FileSystemFileHandle | undefined;

                    if (window.showSaveFilePicker) {
                        fileHandle = await window.showSaveFilePicker({
                            id: 'SuperSplatVideoFileExport',
                            types: filePickerTypes,
                            suggestedName: suggested
                        });

                        writable = await fileHandle.createWritable();
                    }

                    const result = await events.invoke('render.video', videoSettings, writable);

                    // if the render was cancelled, remove the empty file left on disk
                    if (result === false && fileHandle?.remove) {
                        await fileHandle.remove();
                    }
                } catch (error) {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        // user cancelled save dialog
                        return;
                    }

                    await events.invoke('showPopup', {
                        type: 'error',
                        header: 'Failed to render video',
                        message: `'${error.message ?? error}'`
                    });
                }
            }
        });

        events.function('show.vehicleMotionImportDialog', (args?: { text?: string; filename?: string }) => {
            return vehicleMotionImportDialog.show(args);
        });

        events.function('show.cameraMotionImportDialog', (args?: { text?: string; filename?: string }) => {
            return cameraMotionImportDialog.show(args);
        });

        events.function('show.orbitDialog', (args?: Parameters<typeof orbitDialog.show>[0]) => {
            return orbitDialog.show(args);
        });

        events.on('show.about', () => {
            aboutPopup.hidden = false;
        });

        events.function('showPopup', (options: ShowOptions) => {
            return this.popup.show(options);
        });

        // spinner with reference counting to handle nested operations
        const spinner = new Spinner();
        topContainer.append(spinner);

        let spinnerCount = 0;

        events.on('startSpinner', () => {
            spinnerCount++;
            if (spinnerCount === 1) {
                spinner.hidden = false;
            }
        });

        events.on('stopSpinner', () => {
            spinnerCount = Math.max(0, spinnerCount - 1);
            if (spinnerCount === 0) {
                spinner.hidden = true;
            }
        });

        // progress

        const progress = new Progress();

        topContainer.append(progress);

        events.on('progressStart', (header: string, cancellable?: boolean) => {
            progress.hidden = false;
            progress.setHeader(header);
            progress.setText('');
            progress.setProgress(0);
            progress.showCancelButton(!!cancellable);
            progress.onCancel = cancellable ? () => events.fire('progressCancel') : null;
        });

        events.on('progressUpdate', (options: { text?: string, progress?: number }) => {
            if (options.text !== undefined) {
                progress.setText(options.text);
            }
            if (options.progress !== undefined) {
                progress.setProgress(options.progress);
            }
        });

        events.on('progressEnd', () => {
            progress.hidden = true;
            progress.showCancelButton(false);
            progress.onCancel = null;
        });

        // initialize canvas to correct size before creating graphics device etc
        const pixelRatio = window.devicePixelRatio;
        canvas.width = Math.ceil(canvasContainer.dom.offsetWidth * pixelRatio);
        canvas.height = Math.ceil(canvasContainer.dom.offsetHeight * pixelRatio);

        ['contextmenu', 'gesturestart', 'gesturechange', 'gestureend'].forEach((event) => {
            document.addEventListener(event, (e) => {
                e.preventDefault();
            }, true);
        });

        // whenever the canvas container is clicked, set keyboard focus on the body
        canvasContainer.dom.addEventListener('pointerdown', (event: PointerEvent) => {
            // set focus on the body if user is busy pressing on the canvas or a child of the tools
            // element
            if (event.target === canvas || toolsContainer.dom.contains(event.target as Node)) {
                document.body.focus();
            }
        }, true);
    }
}

export { EditorUI };
